// Pull-to-refresh wrapper.
// On mobile, dragging the page down past a threshold triggers onRefresh.
import { useEffect, useRef, useState } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { Loader2 } from "lucide-react";

export default function PullToRefresh({ onRefresh, children, threshold = 70 }) {
  const startY = useRef(null);
  const pulling = useRef(false);
  const y = useMotionValue(0);
  const [refreshing, setRefreshing] = useState(false);
  const [pullDist, setPullDist] = useState(0);

  useEffect(() => {
    const onTouchStart = (e) => {
      if (window.scrollY <= 0 && e.touches.length === 1) {
        startY.current = e.touches[0].clientY;
        pulling.current = true;
      }
    };
    const onTouchMove = (e) => {
      if (!pulling.current || startY.current == null) return;
      const dy = e.touches[0].clientY - startY.current;
      if (dy > 0 && window.scrollY <= 0) {
        // resistance curve
        const eased = Math.min(dy * 0.5, 110);
        y.set(eased);
        setPullDist(eased);
      }
    };
    const onTouchEnd = async () => {
      if (!pulling.current) return;
      pulling.current = false;
      startY.current = null;
      if (pullDist >= threshold) {
        setRefreshing(true);
        animate(y, 50, { type: "spring", stiffness: 380, damping: 30 });
        try { await onRefresh?.(); } finally {
          setRefreshing(false);
          animate(y, 0, { type: "spring", stiffness: 380, damping: 30 });
          setPullDist(0);
        }
      } else {
        animate(y, 0, { type: "spring", stiffness: 380, damping: 30 });
        setPullDist(0);
      }
    };

    window.addEventListener("touchstart", onTouchStart, { passive: true });
    window.addEventListener("touchmove", onTouchMove, { passive: true });
    window.addEventListener("touchend", onTouchEnd);
    return () => {
      window.removeEventListener("touchstart", onTouchStart);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
    };
  }, [onRefresh, pullDist, threshold, y]);

  return (
    <motion.div style={{ y }}>
      {(refreshing || pullDist > 0) && (
        <div
          className="flex flex-col items-center justify-center text-[#8E8E93]"
          style={{ height: 50, marginTop: -50 }}
        >
          <Loader2
            size={20}
            className={refreshing ? "animate-spin" : ""}
            style={{ transform: refreshing ? "none" : `rotate(${pullDist * 3}deg)`, opacity: Math.min(pullDist / threshold, 1) }}
          />
          <div className="text-[11px] mt-1 font-medium">
            {refreshing ? "Refreshing…" : pullDist >= threshold ? "Release to refresh" : "Pull to refresh"}
          </div>
        </div>
      )}
      {children}
    </motion.div>
  );
}
